import {
  gridRowNodeSelector,
  type GridApiPremium,
  type GridColDef,
  type GridDataSource,
  type GridRowSelectionModel,
  type GridSortModel,
} from "@mui/x-data-grid-premium";
import type { ReactNode, RefObject } from "react";
import { useCallback, useMemo, useState } from "react";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import DataGrid from "@entrilia/shared/components/grid/DataGrid";
import {
  DataGridColumnHeadersWithToolbar,
  DataGridHeaderToolbarProvider,
  type DataGridToolbarSelection,
} from "@entrilia/shared/components/grid/DataGridColumnHeadersWithToolbar";
import {
  getRowSelectionModelFromIds,
  getSelectedRowIdsFromModel,
  GRID_DEFAULT_PAGE_SIZE,
} from "@entrilia/shared/components/grid/gridHelper";
import { logError } from "@entrilia/shared/logging";
import type { Field, ObjectFieldValues } from "../../../api/types/objectTypes";
import storage from "../../../storage/storage";
import { useFilterContext } from "../../common/filters/FilterContext";
import { sortAction } from "../../common/filters/filterState";
import { getCheckedGridSx } from "../../common/grid/gridStyles";
import { getEntitiesGridColumns } from "./entitiesGridDataProvider";

const selectionToolbarSlots = { columnHeaders: DataGridColumnHeadersWithToolbar };

interface Props {
  apiRef: RefObject<GridApiPremium | null>;
  dataSource: GridDataSource;
  filterId: string;
  fields: Field[];
  showLogos?: boolean;
  showDuplicateCaptions?: boolean;
  getDetailsPath: (id: string) => string;
  detailsNavigationState?: unknown;
  selection?: DataGridToolbarSelection;
  actionsColumn?: GridColDef<ObjectFieldValues>;
  selectionActions?: ReactNode;
}

const ObjectsGrid = ({
  apiRef,
  dataSource,
  filterId,
  fields,
  showLogos,
  showDuplicateCaptions,
  getDetailsPath,
  detailsNavigationState,
  selection,
  actionsColumn,
  selectionActions,
}: Props) => {
  const { filterState, setFilterState } = useFilterContext<ObjectFieldValues>();

  const [dataSourceError, setDataSourceError] = useState<Error>();

  const columns = useMemo(() => {
    const entityColumns = getEntitiesGridColumns(fields, filterState.visibleColumns, {
      showLogos,
      showDuplicateCaptions,
      getDetailsPath,
      detailsNavigationState,
    });

    return actionsColumn ? [...entityColumns, actionsColumn] : entityColumns;
  }, [
    fields,
    filterState.visibleColumns,
    showLogos,
    showDuplicateCaptions,
    getDetailsPath,
    detailsNavigationState,
    actionsColumn,
  ]);

  const sortModel = useMemo(() => (filterState.sorting ? [filterState.sorting] : []), [filterState.sorting]);

  const handleDataSourceError = useCallback(
    (error: Error) => {
      logError(error, "[ObjectsGrid]");
      setDataSourceError(error);
      storage.clearFilterState(filterId);
    },
    [filterId]
  );

  const handleSortModelChange = useCallback(
    (sortModel: GridSortModel) => {
      setFilterState(sortAction(sortModel));
    },
    [setFilterState]
  );

  const [selectedIds, setSelectedIds] = selection ?? [undefined, undefined];

  const handleRowSelectionModelChange = useCallback(
    (model: GridRowSelectionModel) =>
      setSelectedIds?.(
        getSelectedRowIdsFromModel(model).filter((id) => gridRowNodeSelector(apiRef, id)?.type !== "skeletonRow")
      ),
    [apiRef, setSelectedIds]
  );

  if (dataSourceError) {
    return <DataLoadingFailed title="Loading failed" />;
  }

  return (
    <DataGridHeaderToolbarProvider
      showToolbar={!!selectedIds && selectedIds.length > 0}
      selection={selection}
      actions={selectionActions}
    >
      <DataGrid<ObjectFieldValues>
        apiRef={apiRef}
        sx={selection && getCheckedGridSx}
        columns={columns}
        dataSource={dataSource}
        lazyLoading
        paginationModel={{ page: 0, pageSize: GRID_DEFAULT_PAGE_SIZE }}
        onDataSourceError={handleDataSourceError}
        slots={selection && selectionToolbarSlots}
        disableColumnReorder
        multilineRows={showDuplicateCaptions}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        checkboxSelection={!!selection}
        keepNonExistentRowsSelected={!!selection}
        rowSelectionModel={selectedIds && getRowSelectionModelFromIds(selectedIds)}
        onRowSelectionModelChange={selection && handleRowSelectionModelChange}
      />
    </DataGridHeaderToolbarProvider>
  );
};

export default ObjectsGrid;
